import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { reaction, type IReactionDisposer } from "mobx";
import { DateTime } from "luxon";
import { getLearnMoreHrefWithPrice } from "@/components/modal/proactive-upsell/learn-more-href";
import { APP_CONFIG } from "@/domains/global/config";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { useRefocus } from "@/domains/react/useRefocus";
import { localStorageModule } from "@/modules/local-storage";
import { windowModule } from "@/modules/window";
import {
  type MemAgentHoldToTalkKey,
  type MemAgentHoldToTalkKeySetting,
} from "@/modules/mem-agent/hold-to-talk-key";
import { toastModule } from "@/modules/toast";
import {
  type MemAgentConnectionChannel,
  SettingsMemAgentChannelConnectDialog,
} from "@/pages/settings/settings-mem-agent-channel-connect-dialog";
import { SettingsMemAgentReminderShillSetupDialog } from "@/pages/settings/settings-mem-agent-reminder-shill-setup-dialog";
import {
  type SettingsMemAgentPageKind,
  SettingsMemAgentPageView,
} from "@/pages/settings/settings-mem-agent-page.view";
import { useRemindersEnableActions } from "@/pages/settings/use-reminders-enable-actions";
import { type AppStore, useAppStore } from "@/store";
import type { FloatingAgentPresence } from "@/store/floating-agent/AppStoreFloatingAgentStore";
import type { UpdateMemAgentEmailSenderAllowlistArgs } from "@/store/mem-agent/mem-agent-store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";

/**
 * Builds the short subscription status shown when Mem Agent reminders access is scheduled to end.
 */
const getDisconnectingNoticeLabel = (subscriptionEndsAt: string | null | undefined): string => {
  if (!subscriptionEndsAt) {
    return "Disconnecting soon";
  }

  const now = DateTime.local().startOf("day");
  const periodEnd = DateTime.fromISO(subscriptionEndsAt);
  if (!periodEnd.isValid) {
    return "Disconnecting soon";
  }

  const daysUntilPeriodEnd = Math.round(periodEnd.diff(now, "days").days);
  return daysUntilPeriodEnd > 0
    ? `Disconnecting in ${daysUntilPeriodEnd} days`
    : "Disconnecting soon";
};

/**
 * Coordinates Mem Agent reminders analytics, Slack setup, and unavailable connector modal state.
 */
interface SettingsMemAgentPageProps {
  pageKind?: SettingsMemAgentPageKind;
}

/**
 * Waits for the voice permissions modal to either grant the missing permissions or close.
 */
const waitForVoicePermissionGrant = ({ store }: { store: AppStore }): Promise<boolean> => {
  if (store.systemPermissions.hasRequiredVoicePermissions) {
    return Promise.resolve(true);
  }

  return new Promise(resolve => {
    let dispose: IReactionDisposer | null = null;
    let settled = false;

    const finish = (result: boolean) => {
      if (settled) {
        return;
      }

      settled = true;
      dispose?.();
      resolve(result);
    };

    dispose = reaction(
      () => ({
        hasPermissions: store.systemPermissions.hasRequiredVoicePermissions,
        isModalOpen: store.systemPermissions.ui.isVoiceModalOpen,
      }),
      ({ hasPermissions, isModalOpen }) => {
        if (hasPermissions) {
          finish(true);
          return;
        }

        if (!isModalOpen) {
          finish(false);
        }
      }
    );
  });
};

export const SettingsMemAgentPage = observer(
  ({ pageKind = "general" }: SettingsMemAgentPageProps) => {
    const { store } = useAppStore();
    const { memAgent } = store;
    const { enableOption, isEnablingSlack, isSlackConnected, openSlack } =
      useRemindersEnableActions();
    const hasTrackedPageViewRef = useRef(false);
    const [connectDialogChannel, setConnectDialogChannel] =
      useState<MemAgentConnectionChannel | null>(null);
    const hasActiveAssistant = memAgent.hasAssistant && memAgent.isAssistantEnabled;
    const settingsSource = store.settingsModal.memAgentSettingsSource;
    const isReminderShillSetupDialogOpen = store.settingsModal.isReminderShillSetupDialogOpen;
    const committedHoldToTalkKey = store.spaceAccountSettings.memAgent.holdToTalkKey;
    const committedHoldToTalkIsEnabled = store.spaceAccountSettings.memAgent.holdToTalkIsEnabled;
    const [displayHoldToTalkKey, setDisplayHoldToTalkKey] =
      useState<MemAgentHoldToTalkKeySetting>(committedHoldToTalkKey);
    const [displayHoldToTalkIsEnabled, setDisplayHoldToTalkIsEnabled] = useState(
      committedHoldToTalkIsEnabled
    );
    const [newMessageSoundEnabled, setNewMessageSoundEnabled] = useState(() =>
      localStorageModule.readMemAgentNewMessageSoundEnabled()
    );
    const [isSavingHoldToTalkKey, setIsSavingHoldToTalkKey] = useState(false);
    const reminderShillUpsellAttributionContext =
      store.settingsModal.reminderShillUpsellAttributionContext;

    useEffect(() => {
      setDisplayHoldToTalkKey(committedHoldToTalkKey);
    }, [committedHoldToTalkKey]);

    useEffect(() => {
      setDisplayHoldToTalkIsEnabled(committedHoldToTalkIsEnabled);
    }, [committedHoldToTalkIsEnabled]);

    const userEmail = store.account.myAccount.profileEmailAddress;
    const memAgentEmailChannelAddress = memAgent.assistantSlug
      ? `${memAgent.assistantSlug}@agent.mem.ai`
      : userEmail;
    const protectedEmailSenderAllowlistEntry = {
      kind: "email" as const,
      value: userEmail.trim().toLowerCase(),
    };
    const proactiveAccessScheduledEnd = store.subscriptions.proactiveAccessScheduledEnd;
    const subscriptionNoticeLabel = proactiveAccessScheduledEnd
      ? getDisconnectingNoticeLabel(proactiveAccessScheduledEnd)
      : undefined;
    const handleReload = useCallback(() => {
      void memAgent.reload();
    }, [memAgent]);

    useRefocus(handleReload, { immediate: true });

    useEffect(() => {
      if (hasTrackedPageViewRef.current) return;
      hasTrackedPageViewRef.current = true;

      trackEvent(TrackedEvent.RemindersSettingsPageVisible, {
        source: settingsSource,
        user_email: userEmail,
      });
    }, [settingsSource, userEmail]);

    useEffect(() => {
      void store.subscriptions.ensureProactivePricingAssignment().catch(() => undefined);
    }, [store.subscriptions]);

    const proactivePriceCents = store.subscriptions.proactivePricingAssignment?.display_price_cents;
    const handleOpenLearnMore = useCallback(() => {
      const learnMoreHref = getLearnMoreHrefWithPrice({
        learnMoreHref: APP_CONFIG.URLS.MEM_AGENT_DOCS,
        proactivePriceCents,
      });
      windowModule.openInNewTab({ url: new URL(learnMoreHref) });
    }, [proactivePriceCents]);

    const handleOpenSubscriptionManagement = useCallback(() => {
      store.settingsModal.open(SettingsTabId.SubscriptionManagementPage);
    }, [store.settingsModal]);

    const handleOpenProactiveUpsell = useCallback(() => {
      store.subscriptions.openProactiveUpsell();
    }, [store.subscriptions]);

    const handleRemoveSlack = useCallback(() => {
      void memAgent.disconnectSlack();
    }, [memAgent]);

    const handleConnectWhatsApp = useCallback(async () => {
      if (!store.subscriptions.hasAgentChannelAccess) {
        store.subscriptions.openProactiveUpsell();
        return;
      }

      if (!hasActiveAssistant) {
        await memAgent.setup();
      }
      setConnectDialogChannel("whatsapp");
    }, [hasActiveAssistant, memAgent, store.subscriptions]);

    const handleRemoveWhatsApp = useCallback(() => {
      void memAgent.disconnectWhatsApp();
    }, [memAgent]);

    const handleConnectIMessage = useCallback(async () => {
      if (!store.subscriptions.hasAgentChannelAccess) {
        store.subscriptions.openProactiveUpsell();
        return;
      }
      if (!hasActiveAssistant) {
        await memAgent.setup();
      }
      setConnectDialogChannel("imessage");
    }, [hasActiveAssistant, memAgent, store.subscriptions]);

    const handleRemoveIMessage = useCallback(() => {
      void memAgent.disconnectIMessage();
    }, [memAgent]);

    const handleReauthorizeSlack = useCallback(() => {
      void memAgent.connectSlack({
        source: "settings",
        connectionIntent: "reauthorize",
      });
    }, [memAgent]);

    const handleSaveEmailSenderAllowlist = useCallback(
      (args: UpdateMemAgentEmailSenderAllowlistArgs) => {
        return memAgent.updateEmailSenderAllowlist(args);
      },
      [memAgent]
    );

    const handleFloatingAgentPresenceChange = useCallback(
      (presence: FloatingAgentPresence) => {
        if (presence === store.floatingAgent.presence) return;

        store.floatingAgent.setPresence(presence);
        trackEvent(TrackedEvent.FloatingAgentPresenceSet, {
          presence,
        });
      },
      [store.floatingAgent]
    );

    const handleNewMessageSoundEnabledChange = useCallback((enabled: boolean) => {
      setNewMessageSoundEnabled(enabled);
      localStorageModule.writeMemAgentNewMessageSoundEnabled(enabled);
    }, []);

    useEffect(() => {
      if (pageKind !== "communication-channels") return;

      const pendingChannel = store.settingsModal.pendingMemAgentCommunicationChannelAction;
      if (!pendingChannel) return;

      store.settingsModal.consumePendingMemAgentCommunicationChannelAction();

      if (pendingChannel === "slack") {
        enableOption("Slack");
      } else if (pendingChannel === "whatsapp") {
        void handleConnectWhatsApp();
      } else {
        void handleConnectIMessage();
      }
    }, [
      enableOption,
      handleConnectIMessage,
      handleConnectWhatsApp,
      pageKind,
      store.settingsModal,
      store.settingsModal.pendingMemAgentCommunicationChannelAction,
    ]);

    const saveHoldToTalkKey = useCallback(
      (holdToTalkKey: MemAgentHoldToTalkKeySetting) => {
        if (isSavingHoldToTalkKey || holdToTalkKey === displayHoldToTalkKey) return;

        const previousHoldToTalkKey = displayHoldToTalkKey;
        setDisplayHoldToTalkKey(holdToTalkKey);
        setIsSavingHoldToTalkKey(true);
        void store.spaceAccountSettings
          .updateSettingsUsingSyncOperation({
            mem_agent: {
              hold_to_talk_key: holdToTalkKey,
            },
          })
          .then(
            () => setIsSavingHoldToTalkKey(false),
            () => {
              setDisplayHoldToTalkKey(previousHoldToTalkKey);
              setIsSavingHoldToTalkKey(false);
              toastModule.triggerToast({
                content: "Couldn't save hold-to-talk key. Please try again.",
              });
            }
          );
      },
      [displayHoldToTalkKey, isSavingHoldToTalkKey, store.spaceAccountSettings]
    );
    const handleHoldToTalkKeyChange = useCallback(
      (holdToTalkKey: MemAgentHoldToTalkKey) => {
        saveHoldToTalkKey(holdToTalkKey);
      },
      [saveHoldToTalkKey]
    );
    const handleHoldToTalkEnabledChange = useCallback(
      (enabled: boolean) => {
        if (isSavingHoldToTalkKey || enabled === displayHoldToTalkIsEnabled) return;

        const previousHoldToTalkIsEnabled = displayHoldToTalkIsEnabled;
        setIsSavingHoldToTalkKey(true);
        void (async () => {
          if (enabled && store.interface.isDesktop) {
            await store.systemPermissions.syncPermissions();

            if (!store.systemPermissions.hasRequiredVoicePermissions) {
              store.systemPermissions.ui.openVoicePermissionsModal();
              const granted = await waitForVoicePermissionGrant({ store });

              if (!granted) {
                return;
              }
            }
          }

          setDisplayHoldToTalkIsEnabled(enabled);
          await store.spaceAccountSettings.updateSettingsUsingSyncOperation({
            mem_agent: {
              hold_to_talk_is_enabled: enabled,
            },
          });
        })()
          .catch(() => {
            setDisplayHoldToTalkIsEnabled(previousHoldToTalkIsEnabled);
            toastModule.triggerToast({
              content: "Couldn't save Push-to-Remember shortcut. Please try again.",
            });
          })
          .finally(() => {
            setIsSavingHoldToTalkKey(false);
          });
      },
      [displayHoldToTalkIsEnabled, isSavingHoldToTalkKey, store]
    );

    return (
      <>
        <SettingsMemAgentPageView
          pageKind={pageKind}
          isConnectingSlack={isEnablingSlack}
          isSlackOAuthPending={memAgent.hasPendingSlackOAuthContext}
          isRemovingSlack={memAgent.isRemovingSlack}
          isSlackConnected={isSlackConnected}
          isWhatsAppConnected={memAgent.isWhatsAppConnected}
          whatsAppPhoneNumber={memAgent.whatsAppPhoneNumber}
          isConnectingWhatsApp={memAgent.isConnectingWhatsApp || memAgent.isSettingUp}
          isRemovingWhatsApp={memAgent.isRemovingWhatsApp}
          isIMessageConnected={memAgent.isIMessageConnected}
          imessagePhoneNumber={memAgent.imessagePhoneNumber}
          isConnectingIMessage={memAgent.isConnectingIMessage || memAgent.isSettingUp}
          isRemovingIMessage={memAgent.isRemovingIMessage}
          subscriptionNoticeLabel={subscriptionNoticeLabel}
          hasActiveBillingSubscription={
            store.subscriptions.billingSubscription?.isActivePaid === true
          }
          hasGrantedSubscriptionAccess={store.subscriptions.effectivePlanUsesGrantedAccess}
          emailChannelAddress={memAgentEmailChannelAddress}
          protectedEmailSenderAllowlistEntry={protectedEmailSenderAllowlistEntry}
          emailSenderAllowlist={memAgent.emailSenderAllowlistEntries}
          holdToTalkKey={displayHoldToTalkKey}
          holdToTalkIsEnabled={displayHoldToTalkIsEnabled}
          newMessageSoundEnabled={newMessageSoundEnabled}
          floatingAgentPresence={store.floatingAgent.presence}
          isSavingHoldToTalkKey={isSavingHoldToTalkKey}
          isSavingEmailSenderAllowlist={memAgent.isUpdatingEmailSenderAllowlist}
          showFloatingAgentSettings={store.interface.isDesktop}
          isHoldToTalkSettingDisabled={!store.interface.isDesktop}
          showEmailSenderAllowlist={hasActiveAssistant}
          canSaveEmailSenderAllowlist={hasActiveAssistant}
          onEnableOption={enableOption}
          onOpenSlack={openSlack}
          onOpenLearnMore={handleOpenLearnMore}
          onOpenProactiveUpsell={handleOpenProactiveUpsell}
          onReauthorizeSlack={handleReauthorizeSlack}
          onRemoveSlack={handleRemoveSlack}
          onConnectWhatsApp={handleConnectWhatsApp}
          onRemoveWhatsApp={handleRemoveWhatsApp}
          onConnectIMessage={handleConnectIMessage}
          onRemoveIMessage={handleRemoveIMessage}
          onOpenSubscriptionManagement={handleOpenSubscriptionManagement}
          onSaveEmailSenderAllowlist={handleSaveEmailSenderAllowlist}
          onHoldToTalkKeyChange={handleHoldToTalkKeyChange}
          onHoldToTalkEnabledChange={handleHoldToTalkEnabledChange}
          onNewMessageSoundEnabledChange={handleNewMessageSoundEnabledChange}
          onFloatingAgentPresenceChange={handleFloatingAgentPresenceChange}
        />
        <SettingsMemAgentReminderShillSetupDialog
          isOpen={isReminderShillSetupDialogOpen}
          reminderShillUpsellAttributionContext={reminderShillUpsellAttributionContext}
          userEmail={userEmail}
          onClose={() => store.settingsModal.closeReminderShillSetupDialog()}
        />
        <SettingsMemAgentChannelConnectDialog
          channel={connectDialogChannel}
          onClose={() => setConnectDialogChannel(null)}
        />
      </>
    );
  }
);

export const SettingsMemAgentCommunicationChannelsPage = observer(() => (
  <SettingsMemAgentPage pageKind="communication-channels" />
));
